import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { vdjColor, vdjBeatBreath, VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';

// Koi - seven carp of light swimming loops in a black pond; their passage
// ripples the water, and the surface sways gently with the beat. Deep house,
// downtempo, electronic jazz, lo-fi. CLAUDE.md R1-R11 apply; danceability
// 0.58: R9's 0.55-0.80 band with the LOUNGE preset's habits (fades, long
// loops, a moderate beat, no flash) and R10's 0.35 luminance ceiling.
//
// THE BODY FOLLOWS THE HEAD'S REAL HISTORY. Only the HEAD swims a path: a
// steering agent on the CPU, at a constant speed, aiming at a point ahead
// on its current curve (circle, figure-8, S-loop), its heading turning at
// most MAX_TURN (90 degrees) a second, and pushed away from the cover and
// R6's interface zones. A path change only changes where the head aims:
// position and heading stay continuous, and a sharp change simply takes
// longer. The head's position reaches the GPU as a uniform; a ping-pong
// pass keeps a HISTORY texture (64 x 7, one row per carp, a case every
// 1/30 s whatever the frame rate) - no texture upload per frame. The vertex
// shader places the 48 segments at CONSTANT ARC LENGTH along that history
// (a bounded walk of 64 cases, interpolating inside the case that holds the
// wanted distance, straight on past the oldest): the carp keeps its length
// whatever its speed, and the body goes exactly where the head went. On
// init and on a track change the history is filled by walking BACK along
// each carp's curve, so the carp appear whole.
//
// THE WATER is a DEFORMATION, not a drawing: a wave-equation ripple field
// (wakes of shallow carp, a soft drop on the beat) refracts the carp layer
// by its normals, and a thin specular glint rides the slopes that face the
// light (luminance 0.25 at most, exactly zero on flat water - R3). Lily pads
// are dark silhouettes only: they hide what swims under them, nothing is
// outlined.
//
// THE CARP are white or cream koi patched in TWO palette colours (some also
// black), a different pattern each; a soft light body (3 px edges, a lighter
// dorsal ridge), rounded pectoral fins and a fan tail that flutter; deep
// carp are darker, softer and slightly blue.
//
// They are drawn OVER one another (premultiplied), never added: crossing
// carp, or the seven gathered for a drop, must not sum to white.
//
// Pipeline per frame (inside the display quad's onBeforeRender): history ->
// ripple steps (30 a second) -> carp layer (its own target) -> display.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'koi',
  family: 'organic',
  danceability: 0.58,
  energyLevel: 0.45,
  bpmMin: 70,
  bpmMax: 120,
  movement: 0.45,
  tonality: 0.85,
  // the head history is a float render target, read in the vertex shader
  enabled: FLOAT_TEXTURES_OK
};

const DEFAULT_TIER = 2;
const TIER = {
  2: { ripple: [256, 144], carpRes: 1.0, pads: true },
  1: { ripple: [256, 144], carpRes: 0.75, pads: true },
  0: { ripple: [128, 72], carpRes: 0.75, pads: false }
};

const CARPS = 7;
const SEGMENTS = 48;
const HIST = 64;               // history cases
const HIST_DT = 1 / 30;        // seconds per case
const BODY_LEN = 0.40;         // height units (the frame is 2 high)
const SPEED = [0.22, 0.30];    // height units a second: 64 cases hold 0.47..0.64, more than a body
const MIN_SPEED = 0.21;       // 64 x 1/30 s x 0.21 = 0.45 > BODY_LEN
const MAX_TURN = Math.PI / 2;  // radians a second
const LOOKAHEAD = 0.28;        // the head aims this far ahead on its curve
const HALF_W = 0.050;
const FIN_W = 0.040;
const TAIL_W = 0.072;
const WAVE_AMP = [0.004, 0.024];
const WAVE_K = 1.3;
const WAVE_HZ = 0.9;
const EDGE_PX = 3.0;           // the body's edge melts over this
const DEPTH_EDGE_PX = 7.0;     // ... plus this when deep
const DEPTH_DIM = 0.60;
const BODY_LUM = 0.70;         // R1's sheath luminance
const RIDGE_LUM = 0.86;
const COVER_MARGIN = 0.16;
const ZONE_MARGIN = 0.07;
const FRAME_MARGIN = 0.12;
const AVOID_RANGE = 0.22;      // the head starts turning away this far from a zone
const AVOID_GAIN = 2.5;
const SAFE_FLOOR = 0.50;

// the ripple field
const DAMP = 0.975;
const RIPPLE_HZ = 30;          // field steps a second, whatever the frame rate
const RIPPLE_REFRACT = 0.060;
const WAKE_DEPTH = 0.35;
const WAKE_AMP = 0.022, WAKE_R = 0.026;
const BEAT_AMP = 0.020, BEAT_R = 0.035;
const NORMAL_GAIN = 14.0;      // slope -> normal tilt
const SPEC_POW = 80.0;
const SPEC_GAIN = 1.5;
const SPEC_MAX = 0.25;         // the glint's luminance, at most
const LIGHT = [0.45, 0.55, 0.70];

const PADS = 5;
const PAD_R = [0.07, 0.13];
const PAD_DRIFT = 0.010;

const DROP_BARS = 8;
const PHRASE_FADE_BARS = 4;
const SYNTH_BPM = 100;
const BEATS_PER_PHRASE = 16;
const FALLBACK_COLORS = [0xff6a1a, 0xe8302a, 0xffb43a, 0x2a3a9a, 0xd8402f];
// per carp: freqA, levelA, freqB, levelB, black B patches (kohaku, showa, sanke...)
const PATTERNS = [
  [3.0, 0.44, 5.0, 0.66, 0], [4.0, 0.50, 6.0, 0.62, 1], [2.5, 0.40, 4.5, 0.64, 0], [5.0, 0.52, 3.5, 0.60, 1],
  [3.5, 0.46, 7.0, 0.66, 0], [2.0, 0.38, 5.5, 0.68, 1], [4.5, 0.54, 3.0, 0.60, 0]
];
const SYNTH_HUE = [0.30, 0.45, 0.60, 0.35, 0.50, 0.65, 0.40];

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function smooth(u) { u = clamp01(u); return u * u * (3 - 2 * u); }
function wrapPi(a) { while (a > Math.PI) a -= 2 * Math.PI; while (a < -Math.PI) a += 2 * Math.PI; return a; }

// A curve: [type, cx, cy, rx, ry, dir]  type 0 ellipse, 1 figure-8, 2 S-loop
export function curvePoint(P, th, out) {
  const t = P[0];
  if (t < 0.5) { out[0] = P[1] + P[3] * Math.cos(th); out[1] = P[2] + P[4] * Math.sin(th); }
  else if (t < 1.5) { out[0] = P[1] + P[3] * Math.sin(th); out[1] = P[2] + P[4] * Math.sin(2 * th); }
  else { out[0] = P[1] + P[3] * (Math.cos(th) + 0.18 * Math.cos(3 * th)); out[1] = P[2] + P[4] * (Math.sin(th) - 0.18 * Math.sin(3 * th)); }
  return out;
}
function sdBox(px, py, hx, hy) {
  const qx = Math.abs(px) - hx, qy = Math.abs(py) - hy;
  return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0);
}
function boxNormal(px, py, cx, cy, hx, hy) {
  const e = 1e-3;
  const gx = sdBox(px + e - cx, py - cy, hx, hy) - sdBox(px - e - cx, py - cy, hx, hy);
  const gy = sdBox(px - cx, py + e - cy, hx, hy) - sdBox(px - cx, py - e - cy, hx, hy);
  const l = Math.hypot(gx, gy);
  return l < 1e-9 ? [1, 0] : [gx / l, gy / l];
}

// =============================================================================
// SHADERS
// =============================================================================

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// THE HISTORY. Column 0 is the head now; on a shift every other column
// takes its left neighbour's value.
const historyFragment = `
  precision highp float;
  uniform sampler2D uPrev;
  uniform vec2 uHead[${CARPS}];
  uniform float uShift;
  varying vec2 vUv;
  void main() {
    float col = floor(vUv.x * ${HIST}.0), row = floor(vUv.y * ${CARPS}.0);
    if (col < 0.5) {
      vec2 h = uHead[0];
      for (int k = 1; k < ${CARPS}; k++) if (float(k) == row) h = uHead[k];
      gl_FragColor = vec4(h, 0.0, 1.0);
      return;
    }
    float src = uShift > 0.5 ? col - 1.0 : col;
    gl_FragColor = texture2D(uPrev, vec2((src + 0.5) / ${HIST}.0, (row + 0.5) / ${CARPS}.0));
  }
`;

// the initial fill (init and track change): a texture written once, copied in
const copyFragment = `
  precision highp float;
  uniform sampler2D uSrc;
  varying vec2 vUv;
  void main() { gl_FragColor = texture2D(uSrc, vUv); }
`;

// THE CARP: position.x = s (0 head .. 1 tail), position.y = across (-1, 0, +1)
const carpVertex = `
  precision highp float;
  attribute float aCarp;
  uniform sampler2D uHist;
  uniform float uDepth[${CARPS}];
  uniform float uWavePhase;
  uniform float uAspect;
  uniform vec2 uResolution;
  uniform float uTime;
  varying vec2 vBody;
  varying float vDepth;
  varying float vFin;
  varying float vCarp;
  varying float vHalfPx;

  vec2 H(float c, float row) { return texture2D(uHist, vec2((c + 0.5) / ${HIST}.0, (row + 0.5) / ${CARPS}.0)).xy; }

  void main() {
    float row = aCarp;
    float s = position.x, across = position.y;
    // the wanted arc lengths: here, and a hair toward the tail (the tangent)
    float d0 = s * ${BODY_LEN.toFixed(3)}, d1 = d0 + 0.008;
    vec2 prev = H(0.0, row), p0 = prev, p1 = prev, dir = vec2(-1.0, 0.0);
    float acc = 0.0, f0 = 0.0, f1 = 0.0;
    for (int j = 1; j < ${HIST}; j++) {
      vec2 cur = H(float(j), row);
      float seg = length(cur - prev);
      if (seg > 1e-6) {
        dir = (cur - prev) / seg;
        if (f0 < 0.5 && acc + seg >= d0) { p0 = prev + dir * (d0 - acc); f0 = 1.0; }
        if (f1 < 0.5 && acc + seg >= d1) { p1 = prev + dir * (d1 - acc); f1 = 1.0; }
      }
      acc += seg; prev = cur;
    }
    // past the oldest case: straight on along the last direction
    if (f0 < 0.5) p0 = prev + dir * (d0 - acc);
    if (f1 < 0.5) p1 = prev + dir * (d1 - acc);
    vec2 tg = normalize(p0 - p1 + vec2(1e-6, 0.0));       // toward the head
    vec2 nrm = vec2(-tg.y, tg.x);
    float amp = mix(${WAVE_AMP[0].toFixed(3)}, ${WAVE_AMP[1].toFixed(3)}, s * s);
    vec2 axis = p0 + nrm * amp * sin(${(WAVE_K * 6.2832).toFixed(4)} * s - uWavePhase - row * 1.3);
    // the width: a fusiform body, ROUNDED pectoral lobes that flutter, a fan tail
    float body = ${HALF_W.toFixed(3)} * pow(max(sin(3.14159 * clamp(s * 1.10, 0.0, 1.0)), 0.0), 0.6);
    float u = (s - 0.25) / 0.09;
    float lobe = sqrt(max(1.0 - u * u, 0.0));
    float flutter = 0.80 + 0.20 * sin(uTime * 3.1 + row * 2.0 + across * 1.7);
    float fins = ${FIN_W.toFixed(3)} * lobe * flutter;
    // the fan: opens from the peduncle, widest near the end, closed by a round edge
    float ut = clamp((s - 0.80) / 0.20, 0.0, 1.0);
    float te = max(ut - 0.72, 0.0) / 0.28;
    float tail = ${TAIL_W.toFixed(3)} * pow(ut, 0.9) * sqrt(max(1.0 - te * te, 0.0)) * step(0.80, s) * (0.88 + 0.12 * sin(uTime * 2.3 + row));
    float w = max(body + fins, tail);
    vFin = clamp((max(fins, tail) - body) / max(w, 1e-4), 0.0, 1.0);
    vec2 p = axis + nrm * across * w;
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
    vBody = vec2(s, across);
    float dep = uDepth[0];
    for (int k = 1; k < ${CARPS}; k++) if (float(k) == row) dep = uDepth[k];
    vDepth = dep;
    vCarp = row;
    vHalfPx = w * uResolution.y * 0.5;
  }
`;

const carpFragment = `
  precision mediump float;
  uniform vec3 uBase[${CARPS}];
  uniform vec3 uSpotA[${CARPS}];
  uniform vec3 uSpotB[${CARPS}];
  uniform vec4 uPat[${CARPS}];      // freqA, levelA, freqB, levelB
  uniform float uBlack[${CARPS}];
  uniform float uBreath;
  uniform float uDepthView;
  varying vec2 vBody;
  varying float vDepth;
  varying float vFin;
  varying float vCarp;
  varying float vHalfPx;
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    i = mod(i, 64.0);
    float a = hash21(i), b = hash21(i + vec2(1.0, 0.0)), c = hash21(i + vec2(0.0, 1.0)), d = hash21(i + 1.0);
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  void main() {
    float id = floor(vCarp + 0.5);
    vec3 base = uBase[0], sa = uSpotA[0], sb = uSpotB[0]; vec4 pat = uPat[0]; float blk = uBlack[0];
    for (int k = 1; k < ${CARPS}; k++) if (float(k) == id) { base = uBase[k]; sa = uSpotA[k]; sb = uSpotB[k]; pat = uPat[k]; blk = uBlack[k]; }
    float s = vBody.x, v = abs(vBody.y);
    // the edge melts over EDGE_PX pixels (more when deep)
    float px = ${EDGE_PX.toFixed(1)} + ${DEPTH_EDGE_PX.toFixed(1)} * vDepth;
    float edge = clamp((1.0 - v) * vHalfPx / px, 0.0, 1.0);
    edge = edge * edge * (3.0 - 2.0 * edge);
    edge *= smoothstep(0.0, 0.05, s);               // a rounded nose
    // the koi pattern: patches of two palette colours, black on some
    float seed = id * 13.7;
    float nA = vnoise(vec2(s * pat.x + seed, vBody.y * 1.6 + seed * 0.31));
    float nB = vnoise(vec2(s * pat.z + seed * 1.9 + 7.0, vBody.y * 2.1 - seed * 0.17));
    float onBody = 1.0 - vFin;
    float mA = smoothstep(pat.y, pat.y + 0.07, nA) * onBody;
    float mB = smoothstep(pat.w, pat.w + 0.06, nB) * onBody;
    vec3 c = base * ${BODY_LUM.toFixed(2)};
    c = mix(c, sa * ${BODY_LUM.toFixed(2)}, mA);
    c = mix(c, blk > 0.5 ? c * 0.10 : sb * ${BODY_LUM.toFixed(2)}, mB);
    // the dorsal ridge: softly lighter along the spine, no hard line
    float ridge = (1.0 - smoothstep(0.0, 0.5, v)) * onBody * smoothstep(0.05, 0.22, s) * (1.0 - smoothstep(0.62, 0.85, s));
    c = mix(c, vec3(1.0, 0.97, 0.90) * ${RIDGE_LUM.toFixed(2)}, ridge * 0.35);
    // fins: the base colour, translucent
    c = mix(c, base * 0.80, vFin);
    float alpha = edge * mix(1.0, 0.45, vFin);
    // depth: darker, bluer
    c = mix(c, dot(c, vec3(0.3, 0.5, 0.2)) * vec3(0.55, 0.72, 1.0), vDepth * 0.6) * (1.0 - ${DEPTH_DIM.toFixed(2)} * vDepth);
    if (uDepthView > 0.5) c = mix(vec3(1.0, 0.85, 0.3), vec3(0.15, 0.3, 1.0), vDepth) * 0.7;
    gl_FragColor = vec4(c * alpha * uBreath, alpha);
  }
`;

const rippleFragment = `
  precision highp float;
  uniform sampler2D uPrev;
  uniform vec2 uTexel;
  uniform float uAspect;
  uniform vec4 uSrc[8];         // x, y (height units), radius, amplitude
  uniform float uDamp;
  varying vec2 vUv;
  void main() {
    vec2 c = texture2D(uPrev, vUv).rg;
    float n = texture2D(uPrev, vUv + vec2(uTexel.x, 0.0)).r + texture2D(uPrev, vUv - vec2(uTexel.x, 0.0)).r
            + texture2D(uPrev, vUv + vec2(0.0, uTexel.y)).r + texture2D(uPrev, vUv - vec2(0.0, uTexel.y)).r;
    float h = (n * 0.5 - c.g) * uDamp;
    vec2 p = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    for (int k = 0; k < 8; k++) {
      vec4 s = uSrc[k];
      if (s.w != 0.0) { vec2 d = p - s.xy; h += s.w * exp(-dot(d, d) / (s.z * s.z)); }
    }
    gl_FragColor = vec4(clamp(h, -1.0, 1.0), c.r, 0.0, 1.0);
  }
`;

// THE WATER: refraction by the field's normals, a thin glint on the slopes
// facing the light. Nothing is drawn where the water is flat.
const _L = LIGHT.map((v) => v / Math.hypot(...LIGHT));
const _H = [_L[0], _L[1], _L[2] + 1]; const _Hn = Math.hypot(..._H);
const HALF = _H.map((v) => v / _Hn);
const displayFragment = `
  precision highp float;
  const vec3 HALF_V = vec3(${HALF.map((v) => v.toFixed(5)).join(', ')});
  const float FLAT_SPEC = ${Math.pow(HALF[2], SPEC_POW).toExponential(5)};
  uniform sampler2D uCarps;
  uniform sampler2D uRipple;
  uniform vec2 uRTexel;
  uniform float uRippleOn;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform float uGain;
  varying vec2 vUv;
  void main() {
    vec2 slope = vec2(0.0);
    if (uRippleOn > 0.5) {
      slope = vec2(texture2D(uRipple, vUv + vec2(uRTexel.x, 0.0)).r - texture2D(uRipple, vUv - vec2(uRTexel.x, 0.0)).r,
                   texture2D(uRipple, vUv + vec2(0.0, uRTexel.y)).r - texture2D(uRipple, vUv - vec2(0.0, uRTexel.y)).r);
    }
    vec3 col = texture2D(uCarps, vUv + slope * ${RIPPLE_REFRACT.toFixed(3)}).rgb;
    // the glint: Blinn-Phong on the tilted surface MINUS what flat water
    // gives, so a still surface adds exactly nothing (and skips the maths:
    // most of the pond is flat, and the branch is coherent there)
    if (dot(slope, slope) > 1e-7) {
      vec3 n = normalize(vec3(-slope * ${NORMAL_GAIN.toFixed(1)}, 1.0));
      float spec = max(pow(max(dot(n, HALF_V), 0.0), ${SPEC_POW.toFixed(1)}) - FLAT_SPEC, 0.0);
      col += vec3(0.80, 0.86, 1.0) * min(spec * ${SPEC_GAIN.toFixed(1)}, 1.0) * ${SPEC_MAX.toFixed(2)};
    }
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(sp - uSafeCenter) - uSafeHalfSize;
    float dd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, dd));
    gl_FragColor = vec4(col * uGain * att, 1.0);
  }
`;

// LILY PADS: five small instanced quads drawn OVER the display, multiplying
// what is under them. Inside the full-screen shader the pad loop cost 20 ms
// at tier 1 on the Pi (register pressure, with or without its atan).
const padVertex = `
  attribute float aPad;
  uniform vec4 uPads[${PADS}];
  uniform float uAspect;
  varying vec2 vLocal;
  void main() {
    vec4 pd = uPads[0];
    for (int k = 1; k < ${PADS}; k++) if (float(k) == aPad) pd = uPads[k];
    vLocal = position.xy * 2.0;                      // -1..1, the notch along +x
    float c = cos(pd.w), s = sin(pd.w);
    vec2 p = pd.xy + mat2(c, s, -s, c) * vLocal * pd.z;
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
  }
`;
const padFragment = `
  precision mediump float;
  varying vec2 vLocal;
  void main() {
    float r = length(vLocal);
    float notch = smoothstep(0.984, 0.993, vLocal.x / max(r, 1e-4));
    float inside = (1.0 - smoothstep(0.95, 1.0, r)) * (1.0 - notch);
    gl_FragColor = vec4(0.0, 0.0, 0.0, inside * 0.92);
  }
`;

// =============================================================================

export class KoiVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    this._depthView = urlParam('koidepth') === '1';
    this._rippleOn = urlParam('koiripple') !== '0';
    this._comfort = new VdjComfort(VDJ_PRESETS.lounge.comfort);
  }

  get postProcessing() { return { bloomStrength: 0.40, bloomThreshold: 0.78, vignette: 0.30 }; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._quadGeo = new THREE.PlaneGeometry(1, 1);
    this._aspect = 16 / 9;

    // --- the history: 64 x 7, RGBA32F, ping-pong
    const mkF = () => new THREE.WebGLRenderTarget(HIST, CARPS, { type: THREE.FloatType, format: THREE.RGBAFormat, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    this._hist = [mkF(), mkF()];
    this._hu = { uPrev: { value: null }, uHead: { value: Array.from({ length: CARPS }, () => new THREE.Vector2()) }, uShift: { value: 0 } };
    this._histMat = new THREE.ShaderMaterial({ uniforms: this._hu, vertexShader: quadVertex, fragmentShader: historyFragment, depthTest: false, depthWrite: false, blending: THREE.NoBlending });
    this._histScene = this._quadScene(this._histMat);
    this._fillData = new Float32Array(HIST * CARPS * 4);
    this._fillTex = new THREE.DataTexture(this._fillData, HIST, CARPS, THREE.RGBAFormat, THREE.FloatType);
    this._fillTex.minFilter = this._fillTex.magFilter = THREE.NearestFilter;
    this._copyMat = new THREE.ShaderMaterial({ uniforms: { uSrc: { value: this._fillTex } }, vertexShader: quadVertex, fragmentShader: copyFragment, depthTest: false, depthWrite: false, blending: THREE.NoBlending });
    this._copyScene = this._quadScene(this._copyMat);
    this._histClock = 0;
    this._pendingShifts = 0;

    // --- the carp: one strip, 7 instances
    const n = SEGMENTS + 1;
    const pos = new Float32Array(n * 3 * 3);
    const idx = [];
    for (let k = 0; k < n; k++) {
      for (let j = 0; j < 3; j++) { pos[(k * 3 + j) * 3] = k / SEGMENTS; pos[(k * 3 + j) * 3 + 1] = j - 1; }
      if (k < n - 1) { const a = k * 3, b = (k + 1) * 3; idx.push(a, b, a + 1, a + 1, b, b + 1, a + 1, b + 1, a + 2, a + 2, b + 1, b + 2); }
    }
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setIndex(idx);
    geo.setAttribute('aCarp', new THREE.InstancedBufferAttribute(new Float32Array([0, 1, 2, 3, 4, 5, 6]), 1));
    geo.instanceCount = CARPS;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
    this._carpGeo = geo;
    const cols = () => Array.from({ length: CARPS }, () => new THREE.Color());
    this._cu = {
      uHist: { value: null }, uDepth: { value: new Array(CARPS).fill(0.5) }, uWavePhase: { value: 0 }, uAspect: { value: 16 / 9 },
      uResolution: { value: new THREE.Vector2(1280, 720) }, uTime: { value: 0 },
      uBase: { value: cols() }, uSpotA: { value: cols() }, uSpotB: { value: cols() },
      uPat: { value: PATTERNS.map((p) => new THREE.Vector4(p[0], p[1], p[2], p[3])) }, uBlack: { value: PATTERNS.map((p) => p[4]) },
      uBreath: { value: 1 }, uDepthView: { value: this._depthView ? 1 : 0 }
    };
    this._carpMat = new THREE.ShaderMaterial({
      uniforms: this._cu, vertexShader: carpVertex, fragmentShader: carpFragment,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
      transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide
    });
    const carps = new THREE.Mesh(geo, this._carpMat); carps.frustumCulled = false;
    this._carpScene = new THREE.Scene(); this._carpScene.add(carps);
    this._carpTarget = null;

    // --- the ripple field
    const [rw, rh] = this._layout.ripple;
    const mkH = () => new THREE.WebGLRenderTarget(rw, rh, { type: THREE.HalfFloatType, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    this._rip = [mkH(), mkH()];
    this._ripCleared = false;
    this._ripClock = 0; this._ripSteps = 0;
    this._ru = { uPrev: { value: null }, uTexel: { value: new THREE.Vector2(1 / rw, 1 / rh) }, uAspect: this._cu.uAspect, uSrc: { value: Array.from({ length: 8 }, () => new THREE.Vector4()) }, uDamp: { value: DAMP } };
    this._ripMat = new THREE.ShaderMaterial({ uniforms: this._ru, vertexShader: quadVertex, fragmentShader: rippleFragment, depthTest: false, depthWrite: false, blending: THREE.NoBlending });
    this._ripScene = this._quadScene(this._ripMat);

    // --- the display
    this._du = {
      uCarps: { value: null }, uRipple: { value: null }, uRTexel: { value: new THREE.Vector2(1 / rw, 1 / rh) },
      uRippleOn: { value: this._rippleOn ? 1 : 0 }, uGain: { value: 1 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._displayMat = new THREE.ShaderMaterial({ uniforms: this._du, vertexShader: quadVertex, fragmentShader: displayFragment, depthTest: false, depthWrite: false });
    this._display = new THREE.Mesh(this._quadGeo, this._displayMat);
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._passes(renderer); };
    scene.add(this._display);
    // the pads (tiers 2 and 1)
    this._pu = { uPads: { value: Array.from({ length: PADS }, () => new THREE.Vector4(0, 0, 0.01, 0)) }, uAspect: safeZoneUniforms.uAspect };
    const pg = new THREE.InstancedBufferGeometry();
    pg.index = this._quadGeo.index; pg.setAttribute('position', this._quadGeo.getAttribute('position'));
    pg.setAttribute('aPad', new THREE.InstancedBufferAttribute(new Float32Array([0, 1, 2, 3, 4]), 1));
    pg.instanceCount = PADS;
    pg.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
    this._padGeo = pg;
    this._padMat = new THREE.ShaderMaterial({ uniforms: this._pu, vertexShader: padVertex, fragmentShader: padFragment, transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.ZeroFactor, blendDst: THREE.OneMinusSrcAlphaFactor });
    this._padMesh = new THREE.Mesh(pg, this._padMat);
    this._padMesh.frustumCulled = false;
    this._padMesh.renderOrder = -999;
    this._padMesh.visible = !!this._layout.pads;
    scene.add(this._padMesh);
    this._lumTarget = new THREE.WebGLRenderTarget(8, 8, { depthBuffer: false, stencilBuffer: false });
    this._lumScene = this._quadScene(this._displayMat);
    this._lumPx = new Uint8Array(8 * 8 * 4);
    this._lum = 0;

    // --- the behaviour
    this._time = 0; this._beats = 0; this._wavePhase = 0;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0;
    this._beatAt = -1e9; this._beatSrc = null;
    this._frame = 0;
    this._dropUntil = -1; this._lastDropKey = null;
    this._lastTrackId = undefined;
    this._rnd = mulberry(4242);
    this._carps = [];
    for (let i = 0; i < CARPS; i++) {
      const c = { curve: this._randomCurve(), x: 0, y: 0, a: 0, th: 0, speed: SPEED[0] + this._rnd() * (SPEED[1] - SPEED[0]), depthPhase: this._rnd() * 6.28, depthPeriod: 20 + this._rnd() * 20, preDrop: null };
      this._placeOnCurve(c, this._rnd() * 6.283);
      this._carps.push(c);
    }
    this._colFrom = new Array(CARPS).fill(null); this._colTo = new Array(CARPS).fill(null); this._colT0 = new Array(CARPS).fill(-1e9);
    this._pads = [];
    for (let i = 0; i < PADS; i++) this._pads.push({ x: (this._rnd() * 2 - 1) * 1.5, y: (this._rnd() * 2 - 1) * 0.8, r: PAD_R[0] + this._rnd() * (PAD_R[1] - PAD_R[0]), a: this._rnd() * 6.28, vx: (this._rnd() * 2 - 1) * PAD_DRIFT, vy: (this._rnd() * 2 - 1) * PAD_DRIFT * 0.6 });
    this._applyPalette(null);
    this._fillHistory();
    this._maxUnderCover = 0; this._underCover = new Float32Array(CARPS);
    this._maxTurnRate = 0;
  }

  _quadScene(mat) { const sc = new THREE.Scene(); const m = new THREE.Mesh(this._quadGeo, mat); m.frustumCulled = false; sc.add(m); return sc; }

  // A curve that keeps clear of the cover: a wide ellipse around it, or an 8
  // or an S-loop to one side of it.
  _randomCurve() {
    const r = this._rnd, A = this._aspect;
    const t = Math.floor(r() * 3), side = r() < 0.5 ? -1 : 1, dir = r() < 0.5 ? -1 : 1;
    if (t === 0) return [0, 0, (r() - 0.5) * 0.12, (0.74 + r() * 0.06) * A, 0.66 + r() * 0.06, dir];
    if (t === 1) return [1, side * 0.62 * A, (r() - 0.5) * 0.3, 0.26 * A, 0.32 + r() * 0.12, dir];
    return [2, side * 0.62 * A, (r() - 0.5) * 0.2, (0.20 + r() * 0.04) * A, 0.40 + r() * 0.14, dir];
  }
  _placeOnCurve(c, th) {
    const p = curvePoint(c.curve, th, [0, 0]), q = curvePoint(c.curve, th + 0.01 * c.curve[5], [0, 0]);
    c.x = p[0]; c.y = p[1]; c.a = Math.atan2(q[1] - p[1], q[0] - p[0]); c.th = th;
  }
  // THE FILL: walk back along each carp's curve at its speed, a case every HIST_DT
  _fillHistory() {
    const d = this._fillData, p = [0, 0], q = [0, 0];
    for (let i = 0; i < CARPS; i++) {
      const c = this._carps[i], dir = c.curve[5];
      let th = c.th;
      for (let k = 0; k < HIST; k++) {
        curvePoint(c.curve, th, p);
        const o = (i * HIST + k) * 4;
        d[o] = p[0]; d[o + 1] = p[1]; d[o + 2] = 0; d[o + 3] = 1;
        curvePoint(c.curve, th - 0.001 * dir, q);
        const dl = Math.max(Math.hypot(q[0] - p[0], q[1] - p[1]) / 0.001, 1e-4);
        th -= dir * c.speed * HIST_DT / dl;
      }
      this._hu.uHead.value[i].set(c.x, c.y);
    }
    this._fillTex.needsUpdate = true;
    this._needFill = true;
    this._histClock = 0; this._pendingShifts = 0;
  }

  _applyPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : FALLBACK_COLORS;
    // R4: through vdjColor; each carp's two patch colours a hue gap >= 0.25 apart
    const cols = vivid.map((h) => vdjColor(h, null, 0.85));
    const hsl = { h: 0, s: 0, l: 0 };
    const hues = cols.map((c) => { c.getHSL(hsl); return hsl.h; });
    const gap = (a, b) => { const g = Math.abs(a - b); return Math.min(g, 1 - g); };
    let distinct = 0; for (let k = 0; k < hues.length; k++) if (hues.slice(0, k).every((h) => gap(h, hues[k]) > 0.06)) distinct++;
    this._spots = [];
    for (let i = 0; i < CARPS; i++) {
      const ia = i % cols.length;
      const a = cols[ia].clone();
      // a palette with few hues: spread the first colour a little so the seven differ
      if (distinct < 3) { a.getHSL(hsl); a.setHSL((hsl.h + (i - 3) * 0.025 + 1) % 1, hsl.s, hsl.l); }
      a.getHSL(hsl); const ha = hsl.h;
      const cand = [];
      for (let k = 0; k < cols.length; k++) if (gap(hues[k], ha) >= 0.25) cand.push(cols[k]);
      let b;
      if (cand.length) b = cand[i % cand.length].clone();
      else { b = a.clone(); b.setHSL((ha + SYNTH_HUE[i]) % 1, Math.max(0.85, hsl.s), hsl.l); }
      this._spots.push([a, b]);
      // the base: white or cream, alternating
      this._cu.uBase.value[i].setRGB(1.0, i % 2 ? 0.93 : 0.98, i % 2 ? 0.80 : 0.93);
      this._cu.uSpotA.value[i].copy(a); this._cu.uSpotB.value[i].copy(b);
      this._colTo[i] = null;
    }
  }

  // THE HEAD: aiming at a point ahead on its curve, turning at most MAX_TURN a second
  _steer(c, dt) {
    const P = c.curve, dir = P[5], p = [0, 0], q = [0, 0];
    let bestTh = c.th, bestD = Infinity;
    for (let k = 0; k <= 64; k++) {
      const th = c.th + (k / 64 - 0.5) * 1.2;          // search around the last one
      curvePoint(P, th, p);
      const d = (p[0] - c.x) ** 2 + (p[1] - c.y) ** 2;
      if (d < bestD) { bestD = d; bestTh = th; }
    }
    c.th = bestTh;
    curvePoint(P, bestTh, p); curvePoint(P, bestTh + 0.001 * dir, q);
    const dl = Math.max(Math.hypot(q[0] - p[0], q[1] - p[1]) / 0.001, 1e-4);
    curvePoint(P, bestTh + dir * LOOKAHEAD / dl, q);
    let vx = q[0] - c.x, vy = q[1] - c.y; const vl = Math.hypot(vx, vy) || 1; vx /= vl; vy /= vl;
    // keep clear of the cover, the interface zones and the frame's edges
    // (R19: the cover's box as it sits at the stage centre)
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;
    const boxes = this._zones(); boxes.push([sc.x, sc.y, sh.x, sh.y, COVER_MARGIN]);
    for (const b of boxes) {
      const d = sdBox(c.x - b[0], c.y - b[1], b[2], b[3]) - b[4];
      if (d < AVOID_RANGE) { const nb = boxNormal(c.x, c.y, b[0], b[1], b[2], b[3]); const w = AVOID_GAIN * Math.min(1.5, 1 - d / AVOID_RANGE); vx += nb[0] * w; vy += nb[1] * w; }
    }
    const lx = this._aspect - FRAME_MARGIN, ly = 1 - FRAME_MARGIN;
    const wall = (dist) => (dist < AVOID_RANGE ? AVOID_GAIN * Math.min(1.5, 1 - dist / AVOID_RANGE) : 0);
    vx += wall(c.x + lx) - wall(lx - c.x);
    vy += wall(c.y + ly) - wall(ly - c.y);
    const want = Math.atan2(vy, vx);
    const turn = Math.max(-MAX_TURN * dt, Math.min(MAX_TURN * dt, wrapPi(want - c.a)));
    c.a = wrapPi(c.a + turn);
    if (dt > 0) this._maxTurnRate = Math.max(this._maxTurnRate, Math.abs(turn) / dt);
    const v = c.speed * (c.pace || 1);
    c.x += Math.cos(c.a) * v * dt; c.y += Math.sin(c.a) * v * dt;
  }
  // R6's interface zones in height units: [cx, cy, hx, hy, margin]
  _zones() {
    const A = this._aspect;
    return [[A - 0.22 * A, 1 - 0.12, 0.22 * A, 0.12, ZONE_MARGIN], [-A + 0.08 * A, 1 - 0.12, 0.08 * A, 0.12, ZONE_MARGIN], [A - 0.22 * A, -1 + 0.10, 0.22 * A, 0.10, ZONE_MARGIN]];
  }
  _depth(i) { const c = this._carps[i]; return 0.5 + 0.5 * Math.sin(this._time * 2 * Math.PI / c.depthPeriod + c.depthPhase); }
  _nearestTh(c) {
    let bestTh = 0, bestD = Infinity; const p = [0, 0];
    for (let k = 0; k < 256; k++) { const th = k / 256 * 6.2832; curvePoint(c.curve, th, p); const d = (p[0] - c.x) ** 2 + (p[1] - c.y) ** 2; if (d < bestD) { bestD = d; bestTh = th; } }
    return bestTh;
  }

  _onBeat() {
    this._beatCount++;
    this._beatAt = this._time;
    this._beatSrc = { x: (this._rnd() * 2 - 1) * (this._aspect - 0.2), y: (this._rnd() * 2 - 1) * 0.8, at: this._time };
  }
  _onBar() {
    this._barCount++;
    if (this._time < this._dropUntil) return;
    // one carp's head steers to a new curve; the body follows its history
    const c = this._carps[this._barCount % CARPS];
    c.curve = this._randomCurve();
    c.th = this._nearestTh(c);
  }
  _onPhrase() {
    // one carp's patches fade to another carp's pair of colours
    this._phraseCount++;
    const i = (this._phraseCount * 3) % CARPS, j = (i + 2 + Math.floor(this._rnd() * (CARPS - 3))) % CARPS;
    this._colFrom[i] = [this._cu.uSpotA.value[i].clone(), this._cu.uSpotB.value[i].clone()];
    this._colTo[i] = [this._spots[j][0].clone(), this._spots[j][1].clone()];
    this._colT0[i] = this._time;
  }
  _onDrop() {
    // the seven gather on an ellipse around the cover for DROP_BARS bars
    this._dropUntil = this._time + DROP_BARS * 4 * this._beatPeriod;
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value, A = this._aspect;   // R19: around the stage centre
    // each carp gets a slot, 1/7 of a turn apart, ordered by where it joins
    const rx = Math.min(A - FRAME_MARGIN - 0.10, sh.x + COVER_MARGIN + 0.26), ry = Math.min(1 - FRAME_MARGIN - 0.10, sh.y + COVER_MARGIN + 0.12);
    for (const c of this._carps) { if (!c.preDrop) c.preDrop = c.curve; c.curve = [0, sc.x, sc.y, rx, ry, 1]; c.th = this._nearestTh(c); }
    const order = this._carps.slice().sort((a, b) => a.th - b.th);
    order.forEach((c, k) => { c.slot = k * 2 * Math.PI / CARPS; });
  }

  _ensureCarpTarget(renderer) {
    renderer.getDrawingBufferSize(_size);
    this._aspect = _size.x / _size.y;
    this._cu.uAspect.value = this._aspect;
    const w = Math.max(2, Math.round(_size.x * this._layout.carpRes)), h = Math.max(2, Math.round(_size.y * this._layout.carpRes));
    this._cu.uResolution.value.set(w, h);
    if (this._carpTarget && this._carpTarget.width === w && this._carpTarget.height === h) return;
    if (this._carpTarget) this._carpTarget.dispose();
    this._carpTarget = new THREE.WebGLRenderTarget(w, h, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false });
    this._du.uCarps.value = this._carpTarget.texture;
  }

  _passes(renderer) {
    this._ensureCarpTarget(renderer);
    const prev = renderer.getRenderTarget(), prevAuto = renderer.autoClear;
    renderer.getClearColor(_clear); const pa = renderer.getClearAlpha();
    renderer.autoClear = false;
    if (!this._ripCleared) {
      renderer.setClearColor(0x000000, 1);
      for (const t of this._rip) { renderer.setRenderTarget(t); renderer.clear(true, false, false); }
      this._ripCleared = true;
    }
    if (this._needFill) {
      for (const t of this._hist) { renderer.setRenderTarget(t); renderer.render(this._copyScene, this._ortho); }
      this._needFill = false;
    }
    if (!this._frozen) {
      // 1. the history: the head into column 0, one shift per HIST_DT elapsed
      let shifts = this._pendingShifts;
      do {
        this._hu.uShift.value = shifts > 0 ? 1 : 0;
        this._hu.uPrev.value = this._hist[0].texture;
        renderer.setRenderTarget(this._hist[1]);
        renderer.render(this._histScene, this._ortho);
        this._hist = [this._hist[1], this._hist[0]];
        shifts--;
      } while (shifts > 0);
      this._pendingShifts = 0;
      // 2. the ripple step
      // the sources enter on the first step only
      for (let n = 0; this._rippleOn && n < this._ripSteps; n++) {
        this._ru.uPrev.value = this._rip[0].texture;
        if (n === 1) for (const s of this._ru.uSrc.value) s.w = 0;
        renderer.setRenderTarget(this._rip[1]);
        renderer.render(this._ripScene, this._ortho);
        this._rip = [this._rip[1], this._rip[0]];
      }
      this._ripSteps = 0;
    }
    // 3. the carp layer (redrawn when frozen too: the history holds still)
    this._cu.uHist.value = this._hist[0].texture;
    renderer.setRenderTarget(this._carpTarget);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, false, false);
    renderer.render(this._carpScene, this._ortho);
    this._du.uRipple.value = this._rip[0].texture;
    if (!this._frozen) {
      this._frame++;
      if (this._frame % 30 === 0) {
        renderer.setRenderTarget(this._lumTarget);
        renderer.render(this._lumScene, this._ortho);
        renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 8, 8, this._lumPx);
        let s = 0; for (let i = 0; i < this._lumPx.length; i += 4) s += 0.299 * this._lumPx[i] + 0.587 * this._lumPx[i + 1] + 0.114 * this._lumPx[i + 2];
        this._lum = s / (255 * 64);
        this._comfort.measure(this._lum, 0.5);
      }
    }
    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
    renderer.setClearColor(_clear, pa);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._frozen = paused;               // R11: a freeze, never black
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    // a new track: the carp start again, whole, on their curves
    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first) {
        for (const c of this._carps) { if (c.preDrop) { c.curve = c.preDrop; c.preDrop = null; } this._placeOnCurve(c, this._nearestTh(c)); }
        this._dropUntil = -1;
        this._fillHistory();
      }
    }
    if (paused) return;

    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;   // R11: without a BPM, a calm synthetic tempo
    this._beatPeriod = 60 / bpm;
    this._beats += dt / this._beatPeriod;
    this._wavePhase += dt * Math.PI * 2 * WAVE_HZ * (bpm / 100);
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) { if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true; this._lastBeatPhase = beatPhase; }
    else if (Math.floor(this._beats) !== Math.floor(this._beats - dt / this._beatPeriod)) beatEdge = true;
    if (beatEdge) this._onBeat();
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar(); this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 0) this._onBar();
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % BEATS_PER_PHRASE === 0) this._onPhrase();
    const ev = director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; this._onDrop(); }
    }
    if (this._dropUntil > 0 && this._time >= this._dropUntil) {
      this._dropUntil = -1;
      for (const c of this._carps) if (c.preDrop) { c.curve = c.preDrop; c.preDrop = null; c.th = this._nearestTh(c); }
    }

    // the heads, then the history's clock (R19: the box the carps avoid)
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;
    // during the drop: a carp behind its slot swims faster, one ahead slower
    const dropping = this._time < this._dropUntil;
    if (dropping) {
      let sx = 0, sy = 0;
      for (const c of this._carps) { sx += Math.cos(c.th - c.slot); sy += Math.sin(c.th - c.slot); }
      const ref = Math.atan2(sy, sx);
      // never below MIN_SPEED: 64 cases must still hold a whole body
      for (const c of this._carps) c.pace = Math.max(MIN_SPEED / c.speed, 1 + Math.max(-0.35, Math.min(0.35, 0.6 * wrapPi(ref + c.slot - c.th))));
    }
    for (let i = 0; i < CARPS; i++) {
      const c = this._carps[i];
      if (!dropping) c.pace = 1;
      this._steer(c, dt);
      this._hu.uHead.value[i].set(c.x, c.y);
      this._cu.uDepth.value[i] = this._depth(i);
      // criterion: time with the head inside the cover's own box
      if (Math.abs(c.x - sc.x) < sh.x && Math.abs(c.y - sc.y) < sh.y) { this._underCover[i] += dt; this._maxUnderCover = Math.max(this._maxUnderCover, this._underCover[i]); }
      else this._underCover[i] = 0;
    }
    this._histClock += dt;
    while (this._histClock >= HIST_DT) { this._histClock -= HIST_DT; this._pendingShifts++; }
    this._ripClock += dt;
    while (this._ripClock >= 1 / RIPPLE_HZ) { this._ripClock -= 1 / RIPPLE_HZ; this._ripSteps = Math.min(2, this._ripSteps + 1); }

    // the phrase's colour fade
    for (let i = 0; i < CARPS; i++) {
      if (!this._colTo[i]) continue;
      const f = smooth((this._time - this._colT0[i]) / (PHRASE_FADE_BARS * 4 * this._beatPeriod));
      this._cu.uSpotA.value[i].copy(this._colFrom[i][0]).lerp(this._colTo[i][0], f);
      this._cu.uSpotB.value[i].copy(this._colFrom[i][1]).lerp(this._colTo[i][1], f);
      if (f >= 1) this._colTo[i] = null;
    }
    this._cu.uWavePhase.value = this._wavePhase % (Math.PI * 200);
    this._cu.uTime.value = this._time % 1000;
    this._cu.uBreath.value = vdjBeatBreath(this._time - this._beatAt, VDJ_PRESETS.lounge);
    this._du.uGain.value = this._comfort.gain;

    // the ripple sources: the beat's soft drop, a wake at each shallow head
    const src = this._ru.uSrc.value;
    for (const s of src) s.set(0, 0, 1, 0);
    let k = 0;
    if (this._beatSrc && this._time - this._beatSrc.at < 0.05) src[k++].set(this._beatSrc.x, this._beatSrc.y, BEAT_R, BEAT_AMP);
    for (let i = 0; i < CARPS && k < 8; i++) {
      const d = this._cu.uDepth.value[i];
      if (d < WAKE_DEPTH) src[k++].set(this._carps[i].x, this._carps[i].y, WAKE_R, WAKE_AMP * (1 - d / WAKE_DEPTH));
    }
    const A = this._aspect;
    for (let i = 0; i < PADS; i++) {
      const p = this._pads[i];
      p.x += p.vx * dt; p.y += p.vy * dt; p.a += dt * 0.02;
      if (p.x > A + p.r) p.x = -A - p.r; if (p.x < -A - p.r) p.x = A + p.r;
      if (p.y > 1 + p.r) p.y = -1 - p.r; if (p.y < -1 - p.r) p.y = 1 + p.r;
      this._pu.uPads.value[i].set(p.x, p.y, p.r, p.a);
    }
  }

  getProbe() {
    return {
      t: +this._time.toFixed(3), heads: this._carps.map((c, i) => [+c.x.toFixed(3), +c.y.toFixed(3), +this._depth(i).toFixed(2)]),
      maxUnderCover: +this._maxUnderCover.toFixed(2), maxTurnDegS: +(this._maxTurnRate * 180 / Math.PI).toFixed(1), drop: this._time < this._dropUntil,
      lum: +this._lum.toFixed(3), gain: +this._comfort.gain.toFixed(3), carpTarget: this._carpTarget ? [this._carpTarget.width, this._carpTarget.height] : null
    };
  }

  // for the verification: the history texture as read back
  _readHistory(renderer) {
    const out = new Float32Array(HIST * CARPS * 4);
    renderer.readRenderTargetPixels(this._hist[0], 0, 0, HIST, CARPS, out);
    return out;
  }

  dispose() {
    this._scene.remove(this._display);
    this._scene.remove(this._padMesh); this._padGeo.dispose(); this._padMat.dispose();
    this._carpGeo.dispose(); this._carpMat.dispose(); this._ripMat.dispose(); this._displayMat.dispose(); this._quadGeo.dispose();
    this._histMat.dispose(); this._copyMat.dispose(); this._fillTex.dispose();
    for (const t of this._rip.concat(this._hist)) t.dispose();
    if (this._carpTarget) this._carpTarget.dispose();
    this._lumTarget.dispose();
    this._scene.background = this._prevBackground || null;
  }
}

function mulberry(a) {
  return function () { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
}

const _size = new THREE.Vector2();
const _clear = new THREE.Color();
